import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { SettingsMenu } from './SettingsMenu';
import { NavigationGuideModal } from './NavigationGuideModal';
import { AssistantOverlay } from './AssistantOverlay';
import { SelectionsProvider, useSelections } from './SelectionsContext';
import { SelectionsWidget } from './SelectionsWidget';
import { interiorMenuData, exteriorMenuData, InteriorMenuItem } from '../../types/interiorConfig';
import { LayoutStandard } from './Layouts/LayoutStandard';
import { NavigationRoomsPanel } from './NavigationRoomsPanel';
import { ElevationStrip } from '../shared/ElevationStrip';
import { models } from '../../data/mockData';
import { useTheme } from '../styles/ThemeContext';
import { DesignModeSelectorAlt } from './DesignModeSelectorAlt';
import { DesignMode, NavigationMode } from './DesignModeSelector';
import { Play, Square, Camera, Sparkles } from 'lucide-react';
import { ShutterOverlay } from './AIPhotographer/ShutterOverlay';
import { PhotoReviewPanel, PhotoData } from './AIPhotographer/PhotoReviewPanel';
import { PhotoToast } from './AIPhotographer/PhotoToast';
import { QAReportModal } from './QAReport/QAReportModal';
function PhotographerButton({
  isDark,
  onClick
}: {
  isDark: boolean;
  onClick: () => void;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipOffset, setTooltipOffset] = useState(0);
  useEffect(() => {
    if (isHovered && buttonRef.current && tooltipRef.current) {
      const btnRect = buttonRef.current.getBoundingClientRect();
      const tipRect = tooltipRef.current.getBoundingClientRect();
      const btnCenter = btnRect.top + btnRect.height / 2;
      const tipCenter = tipRect.height / 2;
      setTooltipOffset(btnCenter - tipCenter);
    }
  }, [isHovered]);
  return <div className="fixed top-[380px] right-6 z-50" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-927">
      <motion.button ref={buttonRef} onClick={onClick} initial={{
      scale: 0.9,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} transition={{
      delay: 0.5,
      type: 'spring',
      stiffness: 300,
      damping: 20
    }} whileHover={{
      scale: 1.08
    }} whileTap={{
      scale: 0.92
    }} className={`relative p-4 rounded-2xl shadow-2xl backdrop-blur-xl border transition-colors duration-200 group ${isDark ? 'bg-slate-900/80 border-white/10 hover:bg-slate-800/90 hover:border-cyan-500/30' : 'bg-white/80 border-white/40 hover:bg-white/95 hover:border-teal-400/40'}`} data-id="element-928">
        <Camera className={`w-7 h-7 transition-colors duration-200 ${isDark ? 'text-cyan-400 group-hover:text-cyan-300' : 'text-teal-600 group-hover:text-teal-500'}`} data-id="element-929" />
        <span className={`absolute -top-2 -right-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${isDark ? 'bg-cyan-500 text-white' : 'bg-teal-600 text-white'}`} data-id="element-930">
          AI
        </span>
      </motion.button>

      {/* Rich Marketing Tooltip */}
      <AnimatePresence data-id="element-931">
        {isHovered && <motion.div ref={tooltipRef} initial={{
        opacity: 0,
        x: 8,
        scale: 0.95
      }} animate={{
        opacity: 1,
        x: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        x: 8,
        scale: 0.95
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} className="fixed pointer-events-none z-[9999]" style={{
        top: tooltipOffset || 0,
        right: 'calc(24px + 60px + 16px)'
      }} data-id="element-932">
            <div className={`w-56 p-4 rounded-xl shadow-2xl backdrop-blur-xl border ${isDark ? 'bg-slate-900/95 border-white/10' : 'bg-white/95 border-gray-200'}`} data-id="element-933">
              <div className="flex items-center gap-2 mb-2" data-id="element-934">
                <div className={`p-1 rounded-md ${isDark ? 'bg-cyan-500/20' : 'bg-teal-100'}`} data-id="element-935">
                  <Sparkles className={`w-3.5 h-3.5 ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-936" />
                </div>
                <h4 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-937">
                  AI Photographer
                </h4>
              </div>
              <p className={`text-xs leading-relaxed ${isDark ? 'text-slate-400' : 'text-gray-500'}`} data-id="element-938">
                Capture any view and our AI transforms it into a
                magazine-quality photo, instantly.
              </p>
            </div>
            {/* Arrow */}
            <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full" data-id="element-939">
              <div className={`w-0 h-0 border-t-[6px] border-b-[6px] border-l-[6px] border-transparent ${isDark ? 'border-l-slate-800' : 'border-l-white'}`} data-id="element-940" />
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function InteriorViewerContent() {
  const {
    isDark
  } = useTheme();
  const [selectedItem, setSelectedItem] = useState<InteriorMenuItem | null>(null);
  const [designMode, setDesignMode] = useState<DesignMode>('interior');
  const [navigationMode, setNavigationMode] = useState<NavigationMode>('walk');
  const [selectedElevationId, setSelectedElevationId] = useState<string>(models[0].elevations[0].id);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsTab, setSettingsTab] = useState<'settings' | 'controls'>('settings');
  const [showNavigateModal, setShowNavigateModal] = useState(false);
  const [isAssistantActive, setIsAssistantActive] = useState(false);
  const [showRoomsMenu, setShowRoomsMenu] = useState(false);
  const [showNavigation, setShowNavigation] = useState(false);
  const [showRoomsPanel, setShowRoomsPanel] = useState(false);
  const [isGuidedTourActive, setIsGuidedTourActive] = useState(false);
  const [tourRoomIndex, setTourRoomIndex] = useState(0);
  const [currentCategory, setCurrentCategory] = useState<string>('Kitchen');
  const {
    addSelection,
    selections
  } = useSelections();
  // AI Photographer State
  const [isShutterActive, setIsShutterActive] = useState(false);
  const [showPhotoReview, setShowPhotoReview] = useState(false);
  const [photoToast, setPhotoToast] = useState<{
    isVisible: boolean;
    message: string;
    subtitle: string;
  }>({
    isVisible: false,
    message: '',
    subtitle: ''
  });
  const [photos, setPhotos] = useState<PhotoData[]>([]);
  // QA Report State
  const [showQAReport, setShowQAReport] = useState(false);
  const [qaScreenshotUrl, setQaScreenshotUrl] = useState('');
  // Tour toast notification
  const [tourToast, setTourToast] = useState<{
    message: string;
    subtitle: string;
    type: 'start' | 'stop';
  } | null>(null);
  useEffect(() => {
    if (tourToast) {
      const timer = setTimeout(() => setTourToast(null), 2500);
      return () => clearTimeout(timer);
    }
  }, [tourToast]);
  // Auto-launch guided tour from URL param (?mode=guided)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('mode') === 'guided') {
      const timer = setTimeout(() => {
        handleNavigationModeChange('tour');
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);
  // Mutually exclusive toggle handlers
  const handleSetShowNavigation = (show: boolean) => {
    if (show) setShowRoomsPanel(false);
    setShowNavigation(show);
  };
  const handleSetShowRoomsPanel = (show: boolean) => {
    if (show) setShowNavigation(false);
    setShowRoomsPanel(show);
  };
  // Handle Design Mode Change
  const handleDesignModeChange = (mode: DesignMode) => {
    setDesignMode(mode);
    setShowRoomsPanel(false);
    setShowNavigation(false);
    setSelectedItem(null);
  };
  // Handle Navigation Mode Change
  const handleNavigationModeChange = (mode: NavigationMode) => {
    setNavigationMode(mode);
    // Reset states
    setShowRoomsPanel(false);
    setShowNavigation(false);
    if (mode === 'tour') {
      handleStartGuidedTour();
      setIsStepByStep(false);
    } else if (mode === 'step-by-step') {
      setIsGuidedTourActive(false);
      if (!isStepByStep) {
        handleToggleStepMode();
      }
    } else {
      // Walk mode
      setIsGuidedTourActive(false);
      setIsStepByStep(false);
      setSelectedItem(null);
    }
  };
  // Step-by-Step Mode State
  const [isStepByStep, setIsStepByStep] = useState(false);
  // Flatten menu data for step-by-step navigation
  const flatSteps = useMemo(() => {
    const steps: {
      item: InteriorMenuItem;
      category: string;
    }[] = [];
    const traverse = (items: InteriorMenuItem[], parentCategory: string) => {
      items.forEach(item => {
        if (item.type === 'item' && (item.options || !item.children)) {
          steps.push({
            item,
            category: parentCategory
          });
        }
        if (item.children) {
          traverse(item.children, item.type === 'category' ? item.name : parentCategory);
        }
      });
    };
    traverse(interiorMenuData, 'General');
    return steps;
  }, []);
  const customizationPercent = useMemo(() => {
    if (flatSteps.length === 0) return 0;
    const customizedCount = selections.length;
    return Math.round(customizedCount / flatSteps.length * 100);
  }, [flatSteps.length, selections.length]);
  const currentStepIndex = useMemo(() => {
    if (!selectedItem) return -1;
    return flatSteps.findIndex(step => step.item.id === selectedItem.id);
  }, [selectedItem, flatSteps]);
  const handleNextStep = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < flatSteps.length) {
      const nextStep = flatSteps[nextIndex];
      setSelectedItem(nextStep.item);
      setCurrentCategory(nextStep.category);
    }
  };
  const handlePrevStep = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      const prevStep = flatSteps[prevIndex];
      setSelectedItem(prevStep.item);
      setCurrentCategory(prevStep.category);
    }
  };
  const handleToggleStepMode = () => {
    if (!isStepByStep) {
      if (!selectedItem && flatSteps.length > 0) {
        setSelectedItem(flatSteps[0].item);
        setCurrentCategory(flatSteps[0].category);
      }
      setIsStepByStep(true);
      setNavigationMode('step-by-step');
    } else {
      setIsStepByStep(false);
      setNavigationMode('walk');
    }
  };
  const handleSelectItem = (item: InteriorMenuItem) => {
    setSelectedItem(item);
    const findCategory = (items: InteriorMenuItem[], targetId: string, parentName?: string): string | null => {
      for (const menuItem of items) {
        if (menuItem.id === targetId) {
          return parentName || menuItem.name;
        }
        if (menuItem.children) {
          const found = findCategory(menuItem.children, targetId, menuItem.type === 'category' ? menuItem.name : parentName);
          if (found) return found;
        }
      }
      return null;
    };
    const category = findCategory(interiorMenuData, item.id) || 'General';
    setCurrentCategory(category);
  };
  const handleSelectOption = (optionId: string) => {
    if (selectedItem && selectedItem.options) {
      const option = selectedItem.options.find(o => o.id === optionId);
      if (option) {
        addSelection({
          category: currentCategory,
          itemName: selectedItem.name,
          optionName: option.name,
          optionId: option.id,
          price: 1500,
          thumbnail: option.thumbnail
        });
      }
    }
  };
  const handleExit = () => {
    window.location.reload();
  };
  const handleStartGuidedTour = () => {
    if (isGuidedTourActive) {
      setIsGuidedTourActive(false);
      setNavigationMode('walk');
      setTourToast({
        message: 'Tour Ended',
        subtitle: 'Back to customization',
        type: 'stop'
      });
    } else {
      setTourRoomIndex(0);
      setIsGuidedTourActive(true);
      setNavigationMode('tour');
      setShowNavigation(false);
      setShowRoomsPanel(false);
      setSelectedItem(null);
      setIsStepByStep(false);
      setTourToast({
        message: 'Guided Tour Started',
        subtitle: 'Sit back and enjoy the walkthrough',
        type: 'start'
      });
    }
  };
  const handleNextTourRoom = () => {
    if (tourRoomIndex < tourStops.length - 1) {
      setTourRoomIndex(tourRoomIndex + 1);
    }
  };
  const handlePrevTourRoom = () => {
    if (tourRoomIndex > 0) {
      setTourRoomIndex(tourRoomIndex - 1);
    }
  };
  const handleStopTour = () => {
    setIsGuidedTourActive(false);
    setNavigationMode('walk');
  };
  const tourStops = ['Kitchen', 'Living Room', 'Primary Bedroom', 'Ensuite', 'Bedroom 2', 'Bedroom 3', 'Laundry', 'Basement'];
  const handleSelectRoom = (room: string) => {
    console.log('Teleport to:', room);
  };
  const handleOpenSettings = () => {
    if (showSettings && settingsTab === 'settings') {
      setShowSettings(false);
    } else {
      setSettingsTab('settings');
      setShowSettings(true);
    }
  };
  const handleOpenControls = () => {
    if (showSettings && settingsTab === 'controls') {
      setShowSettings(false);
    } else {
      setSettingsTab('controls');
      setShowSettings(true);
    }
  };
  const handleOpenNavigate = () => {
    setShowNavigateModal(true);
    setShowSettings(false);
  };
  const handleToggleAssistant = () => {
    setIsAssistantActive(prev => {
      if (!prev) {
        setShowSettings(false);
        setShowNavigateModal(false);
        setShowNavigation(false);
        setShowRoomsPanel(false);
      }
      return !prev;
    });
  };
  const handleCapturePhoto = () => {
    // 1. Trigger shutter
    setIsShutterActive(true);
    setTimeout(() => setIsShutterActive(false), 450);
    // 2. Show toast
    setPhotoToast({
      isVisible: true,
      message: 'Photo captured',
      subtitle: "Our AI photographer is at work — we'll email you the result."
    });
    // 3. Add photo to state as processing
    const newPhotoId = Date.now().toString();
    const newPhoto: PhotoData = {
      id: newPhotoId,
      beforeUrl: 'https://cdn.magicpatterns.com/uploads/dZerneoPF8GZ9tH4FE1jcx/Zight_2026-03-10_at_10.44.55_AM.jpg',
      status: 'processing',
      timestamp: Date.now()
    };
    setPhotos(prev => [newPhoto, ...prev]);
    // 4. Open review panel
    setShowPhotoReview(true);
    // 5. Simulate AI processing delay
    setTimeout(() => {
      setPhotos(prev => prev.map(p => p.id === newPhotoId ? {
        ...p,
        status: 'ready',
        afterUrl: 'https://cdn.magicpatterns.com/uploads/2mHHVCTCZ37FufmbTnCa1j/Zight_2026-03-10_at_10.45.18_AM.jpg'
      } : p));
    }, 3000);
  };
  const handleSendEmail = async (email: string) => {
    // Simulate API call
    return new Promise<void>(resolve => setTimeout(resolve, 1500));
  };
  const handleQAReport = () => {
    // 1. Trigger shutter
    setIsShutterActive(true);
    setTimeout(() => setIsShutterActive(false), 450);
    // 2. Capture screenshot (mocked with existing image)
    setQaScreenshotUrl('https://cdn.magicpatterns.com/uploads/dZerneoPF8GZ9tH4FE1jcx/Zight_2026-03-10_at_10.44.55_AM.jpg');
    // 3. Open modal
    setShowQAReport(true);
  };
  const handleSubmitQAReport = async (data: any) => {
    console.log('Submitting QA Report:', data);
    return new Promise<void>(resolve => setTimeout(resolve, 1500));
  };
  const currentMenuData = designMode === 'exterior' ? exteriorMenuData : interiorMenuData;
  const bgImage = designMode === 'exterior' ? 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1920&q=80' : 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=1920&q=80';
  const layoutProps = {
    menuData: currentMenuData,
    selectedItem,
    onSelectItem: handleSelectItem,
    onSelectOption: handleSelectOption,
    onCloseOption: () => setSelectedItem(null),
    currentCategory,
    showSettings,
    setShowSettings,
    showRoomsMenu,
    setShowRoomsMenu,
    onExit: handleExit,
    showNavigation,
    setShowNavigation: handleSetShowNavigation,
    showRoomsPanel,
    setShowRoomsPanel: handleSetShowRoomsPanel,
    onSelectRoom: handleSelectRoom,
    onStartGuidedTour: handleStartGuidedTour,
    isGuidedTourActive,
    tourRoomIndex,
    totalTourRooms: tourStops.length,
    currentTourRoomName: tourStops[tourRoomIndex],
    onNextTourRoom: handleNextTourRoom,
    onPrevTourRoom: handlePrevTourRoom,
    onStopTour: handleStopTour,
    isStepByStep,
    stepIndex: currentStepIndex !== -1 ? currentStepIndex : 0,
    totalSteps: flatSteps.length,
    currentStepItem: selectedItem,
    currentStepCategory: currentCategory,
    onNextStep: handleNextStep,
    onPrevStep: handlePrevStep,
    onToggleStepMode: handleToggleStepMode,
    designMode,
    onDesignModeChange: handleDesignModeChange,
    customizationPercent,
    onOpenSettings: handleOpenSettings,
    onOpenControls: handleOpenControls,
    onOpenNavigate: handleOpenNavigate,
    settingsTab,
    isAssistantActive,
    onToggleAssistant: handleToggleAssistant,
    onPhotographer: handleCapturePhoto,
    onQAReport: handleQAReport,
    hideAssistantButton: true
  };
  return <div className="relative w-full h-screen overflow-hidden bg-slate-900 font-sans" data-id="element-941">
      {/* 3D Viewer Background */}
      <div className="absolute inset-0 z-0" data-id="element-942">
        <img src={bgImage} alt={designMode === 'exterior' ? 'Exterior view' : 'Interior view'} className="w-full h-full object-cover transition-opacity duration-500" data-id="element-943" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40" data-id="element-944" />
      </div>

      {/* Design Mode Selector (Left Rail) */}
      <DesignModeSelectorAlt activeMode={designMode} onSelectMode={handleDesignModeChange} navigationMode={navigationMode} onNavigationModeChange={handleNavigationModeChange} isAssistantActive={isAssistantActive} onToggleAssistant={handleToggleAssistant} onNavigatePanelOpen={() => {
      setShowSettings(false);
      setShowNavigateModal(false);
      setShowNavigation(false);
      setShowRoomsPanel(false);
    }} data-id="element-945" />

      {/* Layout */}
      <LayoutStandard {...layoutProps} data-id="element-946" />

      {/* Exterior Elevation Selector Overlay */}
      <AnimatePresence data-id="element-947">
        {designMode === 'exterior' && <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-30" data-id="element-948">
            <div className={`p-4 rounded-xl backdrop-blur-xl border shadow-2xl ${isDark ? 'bg-slate-900/80 border-white/10' : 'bg-white/80 border-white/40'}`} data-id="element-949">
              <h3 className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-white/70' : 'text-gray-500'}`} data-id="element-950">
                Select Elevation
              </h3>
              <ElevationStrip elevations={models[0].elevations} selectedElevationId={selectedElevationId} onSelect={elevation => setSelectedElevationId(elevation.id)} data-id="element-951" />
            </div>
          </div>}
      </AnimatePresence>

      {/* Global Overlays */}
      <SettingsMenu isOpen={showSettings} onClose={() => setShowSettings(false)} initialTab={settingsTab} onTabChange={tab => setSettingsTab(tab)} data-id="element-952" />

      <NavigationGuideModal isOpen={showNavigateModal} onClose={() => setShowNavigateModal(false)} data-id="element-953" />

      <AssistantOverlay isActive={isAssistantActive} onDeactivate={() => setIsAssistantActive(false)} data-id="element-954" />

      <SelectionsWidget onSwap={(cat, itemName) => {
      const findMenuItem = (items: InteriorMenuItem[], target: string): InteriorMenuItem | null => {
        for (const item of items) {
          if (item.name === target) return item;
          if (item.children) {
            const found = findMenuItem(item.children, target);
            if (found) return found;
          }
        }
        return null;
      };
      const menuItem = findMenuItem(interiorMenuData, itemName);
      if (menuItem) {
        handleSelectItem(menuItem);
      }
    }} isGalleryOpen={!!selectedItem?.options} data-id="element-955" />

      {/* AI Photographer Components */}
      <ShutterOverlay isActive={isShutterActive} data-id="element-956" />

      <PhotoToast isVisible={photoToast.isVisible} message={photoToast.message} subtitle={photoToast.subtitle} onDismiss={() => setPhotoToast(prev => ({
      ...prev,
      isVisible: false
    }))} data-id="element-957" />

      <PhotoReviewPanel isOpen={showPhotoReview} onClose={() => setShowPhotoReview(false)} photos={photos} onSendEmail={handleSendEmail} data-id="element-958" />

      <QAReportModal isOpen={showQAReport} onClose={() => setShowQAReport(false)} screenshotUrl={qaScreenshotUrl} onSubmit={handleSubmitQAReport} data-id="element-959" />

      {/* Isolated AI Photographer Button with Rich Tooltip */}
      <PhotographerButton isDark={isDark} onClick={handleCapturePhoto} data-id="element-960" />

      {/* Tour Toast Notification */}
      <AnimatePresence data-id="element-961">
        {tourToast && <motion.div initial={{
        opacity: 0,
        y: -20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -20,
        scale: 0.95
      }} transition={{
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }} className="fixed top-8 left-1/2 -translate-x-1/2 z-[100]" data-id="element-962">
            <div className={`flex items-center gap-3 px-5 py-3 rounded-xl backdrop-blur-xl border shadow-2xl ${isDark ? 'bg-slate-900/90 border-white/10' : 'bg-white/90 border-white/40'}`} data-id="element-963">
              <div className={`p-2 rounded-lg ${tourToast.type === 'start' ? isDark ? 'bg-cyan-500/20' : 'bg-teal-500/15' : isDark ? 'bg-white/10' : 'bg-gray-100'}`} data-id="element-964">
                {tourToast.type === 'start' ? <Play className={`w-4 h-4 ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-965" /> : <Square className={`w-4 h-4 ${isDark ? 'text-white/60' : 'text-gray-500'}`} data-id="element-966" />}
              </div>
              <div data-id="element-967">
                <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-968">
                  {tourToast.message}
                </p>
                <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`} data-id="element-969">
                  {tourToast.subtitle}
                </p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function InteriorViewer() {
  return <SelectionsProvider data-id="element-970">
      <InteriorViewerContent data-id="element-971" />
    </SelectionsProvider>;
}